import fp from 'lodash/fp';
import React, { useState, ComponentProps, ComponentType } from 'react';

import {
    Heading, Text, Button, InPlacePopup,
    TransitionComponent, Refresh, animation,
    useUpdateOnlyEffect,
} from 'Modules/ui';
import { useAction } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';

import { paginationActions } from '../../state/pagination/actions';
import styles from './index.module.scss';
import { usePagination } from '../../utils';


export type PaginationHeaderType = {
    path: string;
    title: string;
    updateAnytime?: boolean;
    buttons?: ComponentType;
}

export const PaginationHeader = React.memo<PaginationHeaderType>(({
    path, title,
    updateAnytime = false,
    buttons: Buttons,
}) => {
    const dirty = usePagination(path, 'dirty');
    const total = usePagination(path, 'total');

    const [notifyDirty, setNotifyDirty] = useState(false);
    useUpdateOnlyEffect(() => {
        if (!dirty)
            return undefined;
        const hideTimer = setTimeout(() => setNotifyDirty(false), 10000);
        setNotifyDirty(true);
        return () => clearTimeout(hideTimer);
    }, [dirty]);

    const refresh = useAction(
        () => paginationActions.refresh(path, {}),
        [path],
    );

    const { t } = useTranslation('paginationHeader');

    return (
        <div className={styles.paginationHeader}>
            <div>
                <div className={styles.header}>
                    <Heading<ComponentProps<'h1'>> component='h1' headingType='h1Element' ellipsis className={styles.title}>
                        {title}
                    </Heading>
                    <Text textType='info' component='div' className={styles.total}>
                        {t({
                            key: 'total',
                            [fp.isNumber(total) ? 'values' : 'translatedValues']: {
                                count: fp.isNumber(total) ? total : 'unknown',
                            },
                        })}
                    </Text>
                </div>
                {(updateAnytime || dirty) && (
                    <div className={styles.dirty}>
                        <Button
                            variant='auxiliary'
                            className={styles.icon}
                            onClick={refresh}
                        >
                            <Refresh />
                        </Button>
                        {dirty && (
                            <TransitionComponent
                                in={notifyDirty}
                                timeout={animation}
                                keepInDOM
                            >
                                <Text<ComponentProps<typeof InPlacePopup>>
                                    textType='light'
                                    component={InPlacePopup}
                                    className={styles.notification}
                                    alignment='top'
                                    position={0.3}
                                >
                                    {t('updated')}
                                </Text>
                            </TransitionComponent>
                        )}
                    </div>
                )}
                {Buttons && <Buttons />}
            </div>
        </div>
    );
});
